import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, BookOpen, Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ArticleListConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="divide-y divide-border border-y border-border">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="py-8 px-4 sm:px-6 animate-pulse">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-5 w-20 bg-muted rounded-full" />
              <div className="h-4 w-28 bg-muted rounded-md" />
            </div>
            <div className="h-7 w-3/4 bg-muted rounded-md mb-3" />
            <div className="h-4 w-full bg-muted rounded-md mb-2" />
            <div className="h-4 w-2/3 bg-muted rounded-md mb-6" />
            <div className="flex items-center gap-6">
              <div className="h-4 w-24 bg-muted rounded-md" />
              <div className="h-4 w-20 bg-muted rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center my-8">
        <p className="text-sm font-medium text-destructive mb-3">Unable to retrieve publication entries.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry connection
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border-y border-border py-16 text-center">
        <BookOpen className="size-8 mx-auto text-muted-foreground/60 mb-3" />
        <h3 className="text-lg font-serif font-semibold text-foreground">No publications found</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
          New editorial analysis and strategic viewpoints are published weekly. Please check back shortly.
        </p>
      </div>;
  }
  return <div className="space-y-8">
      <div className="divide-y divide-border border-y border-border">
        {posts.map((post, i) => {
        const categoryName = post.categories?.[0]?.name || "Strategic Analysis";
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        }) : "Recent Issue";
        const authorName = post.author?.name || "Editorial Board";
        const readingTime = post.customFields?.reading_time || "6 min read";
        return <article key={post.id || i} data-index={i} className="group py-8 px-3 sm:px-6 transition-colors duration-200 hover:bg-muted/40">
              <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 mb-3">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider uppercase px-3 py-0.5">
                    {categoryName}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-mono flex items-center gap-1.5">
                    <SafeHydrate><Calendar className="size-3 text-muted-foreground" /></SafeHydrate>
                    {formattedDate}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground font-mono flex items-center gap-1.5">
                  <Clock className="size-3 text-muted-foreground" />
                  {readingTime}
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-start">
                <div className="lg:col-span-8">
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground leading-snug tracking-tight group-hover:text-primary transition-colors">
                    <Link to={post.link || "#"} className="hover:underline">
                      {post.title}
                    </Link>
                  </h3>
                  {post.excerpt && <div className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed line-clamp-2" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}
                  {!post.excerpt && <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                      Rigorous quantitative appraisal examining key capital allocation, revenue operations, and systemic enterprise trends across modern corporate sectors.
                    </p>}
                </div>

                <div className="lg:col-span-4 flex flex-row lg:flex-col justify-between lg:items-end gap-4 pt-2 lg:pt-0">
                  <div className="text-xs text-muted-foreground">
                    <span className="block font-medium text-foreground">By {authorName}</span>
                    <span className="text-muted-foreground font-mono text-[11px]">Senior Analyst & Fellow</span>
                  </div>

                  <Button asChild variant="ghost" size="sm" className="text-foreground hover:text-foreground font-medium text-sm group/btn px-2">
                    <Link to={post.link || "#"} className="inline-flex items-center gap-1.5">
                      <span>Read article</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-between pt-4">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="rounded-sm">
            <ChevronLeft className="size-4" />
            Previous
          </Button>
          <span className="text-xs font-mono text-muted-foreground">
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="rounded-sm">
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function Articles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="article-list" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        <div className="mb-10 sm:mb-14">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Editorial Archive
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight">
                Analysis and viewpoints across B2B ecosystems
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl">
                Explore peer-reviewed perspectives, market commentaries, and strategic breakdowns written by industry practitioners.
              </p>
            </div>
            <Button asChild variant="outline" className="shrink-0 rounded-sm self-start md:self-auto">
              <Link to="/ResourcesPage">Browse all resources</Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticleListConsumer />
        </PostsProvider>
      </div>
    </section>;
}